import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, UsersIcon, EditIcon, TrashIcon, SaveIcon, XIcon, PlusIcon } from 'lucide-react';
import { API_URL, fetchWithAuth } from '../services/ApiService';
import { useAuth } from '../contexts/AuthContext';
import CommentsSection from '../components/Comments/CommentsSection';
import MentionInput from '../components/Comments/MentionInput';
import '../components/Comments/Comments.css';

const LineupPage = () => {
  const { bandId, lineupId } = useParams();
  const navigate = useNavigate();
  const { currentUser, isAuthenticated } = useAuth();
  const [band, setBand] = useState(null);
  const [lineup, setLineup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [isEditingMembers, setIsEditingMembers] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editMembers, setEditMembers] = useState([]);
  const [editDescriptionMentions, setEditDescriptionMentions] = useState([]);
  const [editMemberMentions, setEditMemberMentions] = useState([]);

  useEffect(() => {
    if (bandId && lineupId) {
      fetchLineupData();
    }
  }, [bandId, lineupId]);

  const fetchLineupData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch band data (which includes lineups)
      const bandResponse = await fetchWithAuth(`${API_URL}/bands/${bandId}`);
      if (bandResponse && bandResponse.data) {
        setBand(bandResponse.data);
        setUserRole(bandResponse.data.user_role);
        
        // Find the specific lineup
        const foundLineup = bandResponse.data.lineups?.find(l => l._id.toString() === lineupId);
        if (foundLineup) {
          setLineup(foundLineup);
          // Initialize edit state
          setEditName(foundLineup.name || '');
          setEditDescription(foundLineup.description || '');
          setEditMembers([...(foundLineup.members || [])]);
          setEditDescriptionMentions([...(foundLineup.descriptionMentions || [])]);
          setEditMemberMentions([...(foundLineup.memberMentions || [])]);
        } else {
          setError('Lineup not found');
        }
      } else {
        setError('Band not found');
      }
    } catch (err) {
      console.error('Error fetching lineup data:', err);
      setError('Failed to load lineup data');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditInfo = () => {
    setIsEditingInfo(true);
    setEditName(lineup.name || '');
    setEditDescription(lineup.description || '');
    setEditDescriptionMentions([...(lineup.descriptionMentions || [])]);
  };

  const handleSaveInfo = async () => {
    try {
      const updatedLineup = await fetchWithAuth(`${API_URL}/bands/${bandId}/lineups/${lineupId}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: editName,
          description: editDescription,
          descriptionMentions: editDescriptionMentions
        })
      });

      // Update the lineup state with the new data
      setLineup(prev => ({
        ...prev,
        name: editName,
        description: editDescription,
        descriptionMentions: editDescriptionMentions
      }));
      setIsEditingInfo(false);
    } catch (err) {
      console.error('Error updating lineup info:', err);
      alert('Failed to update lineup. Please try again.');
    }
  };

  const handleCancelEditInfo = () => {
    setIsEditingInfo(false);
    setEditName(lineup.name || '');
    setEditDescription(lineup.description || '');
    setEditDescriptionMentions([...(lineup.descriptionMentions || [])]);
  };

  const handleStartEditMembers = () => {
    setIsEditingMembers(true);
    setEditMembers([...(lineup.members || [])]);
    setEditMemberMentions([...(lineup.memberMentions || [])]);
  };

  const handleSaveMembers = async () => {
    try {
      const updatedLineup = await fetchWithAuth(`${API_URL}/bands/${bandId}/lineups/${lineupId}`, {
        method: 'PUT',
        body: JSON.stringify({
          members: editMembers,
          memberMentions: editMemberMentions
        })
      });

      // Update the lineup state with the new data
      setLineup(prev => ({
        ...prev,
        members: editMembers,
        memberMentions: editMemberMentions
      }));
      setIsEditingMembers(false);
    } catch (err) {
      console.error('Error updating lineup members:', err);
      alert('Failed to update lineup members. Please try again.');
    }
  };

  const handleCancelEditMembers = () => {
    setIsEditingMembers(false);
    setEditMembers([...(lineup.members || [])]);
    setEditMemberMentions([...(lineup.memberMentions || [])]);
  };

  const handleAddMember = () => {
    setEditMembers(prev => [...prev, { name: '', role: '' }]);
  };

  const handleRemoveMember = (index) => {
    setEditMembers(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateMember = (index, field, value) => {
    setEditMembers(prev => prev.map((member, i) => 
      i === index ? { ...member, [field]: value } : member
    ));
  };

  // Helper functions for member mentions
  const getMemberMentions = (memberIndex, field) => {
    return editMemberMentions.filter(m => 
      m.memberIndex === memberIndex && m.field === field
    ).map(m => ({ id: m.userId, username: m.username }));
  };

  const updateMemberMentions = (memberIndex, field, mentions) => {
    setEditMemberMentions(prev => {
      // Remove existing mentions for this member field
      const filtered = prev.filter(m => 
        !(m.memberIndex === memberIndex && m.field === field)
      );
      // Add new mentions
      const newMentions = mentions.map(mention => ({
        memberIndex,
        field,
        userId: mention.id,
        username: mention.username
      }));
      return [...filtered, ...newMentions];
    });
  };

  // Function to render text with clickable @mentions
  const renderTextWithMentions = (text, mentions = []) => {
    if (!text) return null;
    if (!mentions || mentions.length === 0) return text;

    // Create a regex pattern to match @usernames
    const mentionPattern = /@([a-zA-Z0-9_]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionPattern.exec(text)) !== null) {
      const username = match[1];
      const mention = mentions.find(m => m.username === username);
      
      // Add text before the mention
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      // Add the mention (clickable if we have user data)
      if (mention) {
        parts.push(
          <Link
            key={`mention-${mention.id}-${match.index}`}
            to={`/profile/${mention.id}`}
            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
          >
            @{username}
          </Link>
        );
      } else {
        // If mention not found in our list, render as plain text
        parts.push(`@${username}`);
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };

  const handleDeleteLineup = async () => {
    if (!window.confirm(`Are you sure you want to delete the lineup "${lineup.name}"?`)) {
      return;
    }

    try {
      await fetchWithAuth(`${API_URL}/bands/${bandId}/lineups/${lineupId}`, {
        method: 'DELETE'
      });
      
      // Navigate back to band page
      navigate(`/band/${bandId}`, { 
        state: { message: 'Lineup deleted successfully' }
      });
    } catch (err) {
      console.error('Error deleting lineup:', err);
      alert('Failed to delete lineup. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lineup...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to={`/band/${bandId}`}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Band
          </Link>
        </div>
      </div>
    );
  }

  if (!band || !lineup) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Lineup Not Found</h2>
          <p className="text-gray-600 mb-6">The lineup you're looking for doesn't exist.</p>
          <Link
            to={`/band/${bandId}`}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Band
          </Link>
        </div>
      </div>
    );
  }

  const canEditLineup = userRole && ['admin', 'member'].includes(userRole);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={`/band/${bandId}`}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to {band.name}
          </Link>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            {isEditingInfo ? (
              // Edit mode for lineup info
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lineup Name
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter lineup name..."
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (optional)
                  </label>
                  <MentionInput
                    value={editDescription}
                    onChange={setEditDescription}
                    mentions={editDescriptionMentions}
                    onMentionsChange={setEditDescriptionMentions}
                    placeholder="Enter lineup description... (type @ to mention users)"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleSaveInfo}
                    disabled={!editName.trim()}
                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300"
                  >
                    <SaveIcon className="w-4 h-4 mr-2" />
                    Save
                  </button>
                  <button
                    onClick={handleCancelEditInfo}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <XIcon className="w-4 h-4 mr-2" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // Display mode for lineup info
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <UsersIcon className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">{lineup.name}</h1>
                    {lineup.description && (
                      <div className="text-gray-600 mt-1">
                        {renderTextWithMentions(lineup.description, lineup.descriptionMentions)}
                      </div>
                    )}
                    <p className="text-sm text-gray-500 mt-1">
                      Created {new Date(lineup.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                {canEditLineup && (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleStartEditInfo}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <EditIcon className="w-4 h-4 mr-2" />
                      Edit Info
                    </button>
                    <button
                      onClick={handleDeleteLineup}
                      className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                    >
                      <TrashIcon className="w-4 h-4 mr-2" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Members */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Lineup Members</h2>
            {canEditLineup && !isEditingMembers && (
              <button
                onClick={handleStartEditMembers}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <EditIcon className="w-4 h-4 mr-2" />
                Edit Members
              </button>
            )}
          </div>
          
          {isEditingMembers ? (
            // Edit mode for members
            <div>
              <div className="space-y-3 mb-4">
                {editMembers.map((member, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <div className="mb-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Member Name
                        </label>
                        <MentionInput
                          value={member.name}
                          onChange={(value) => handleUpdateMember(index, 'name', value)}
                          mentions={getMemberMentions(index, 'name')}
                          onMentionsChange={(mentions) => updateMemberMentions(index, 'name', mentions)}
                          placeholder="Member name... (type @ to mention users)"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Role/Instrument
                        </label>
                        <MentionInput
                          value={member.role}
                          onChange={(value) => handleUpdateMember(index, 'role', value)}
                          mentions={getMemberMentions(index, 'role')}
                          onMentionsChange={(mentions) => updateMemberMentions(index, 'role', mentions)}
                          placeholder="Role/instrument... (type @ to mention users)"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveMember(index)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <XIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="flex space-x-2 mb-4">
                <button
                  onClick={handleAddMember}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Member
                </button>
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveMembers}
                  className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <SaveIcon className="w-4 h-4 mr-2" />
                  Save Members
                </button>
                <button
                  onClick={handleCancelEditMembers}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <XIcon className="w-4 h-4 mr-2" />
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            // Display mode for members
            <>
              {lineup.members && lineup.members.length > 0 ? (
                <div className="space-y-3">
                  {lineup.members.map((member, index) => (
                    <div key={index} className="flex items-center justify-between py-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-600">
                            {member.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {renderTextWithMentions(
                              member.name, 
                              (lineup.memberMentions || []).filter(m => m.memberIndex === index && m.field === 'name')
                                .map(m => ({ id: m.userId, username: m.username }))
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {renderTextWithMentions(
                              member.role,
                              (lineup.memberMentions || []).filter(m => m.memberIndex === index && m.field === 'role')
                                .map(m => ({ id: m.userId, username: m.username }))
                            )}
                          </div>
                        </div>
                      </div>
                      {member.userId && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Band Member
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <UsersIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p>No members assigned to this lineup yet.</p>
                  {canEditLineup && (
                    <button
                      onClick={handleStartEditMembers}
                      className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Add members
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Comments Section */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <CommentsSection
            objectType="lineup"
            objectId={lineupId}
            allowComments={true}
          />
        </div>
      </div>
    </div>
  );
};

export default LineupPage;